import { _defaultConfigOptions } from "./constants";

export default class AriaControl {
  /**
   * Actions defined for controls that modify DOM and need to update ARIA attributes
   */
  static validActions = {
    expand: "EXPAND",
    collapse: "COLLAPSE",
  };

  /**
   * Default config that we control the ARIA attributes with
   */
  static defaultConfigOptions = _defaultConfigOptions;

  /**
   * Constructor will handle configuring intial Aria attributes
   *
   * @param {defaultConfigOptions} config
   */
  constructor(configOptions = this.defaultConfigOptions) {
    // Initial ARIA configuration
    const { control, root } = configOptions;
    this.control = control;
    if (root) {
      // Only search within root
      this.targets = root.querySelectorAll(
        `[data-controlled-by*=${this.control.id}]`
      );
    } else {
      // Only search the whole page
      this.targets = document.querySelectorAll(
        `[data-controlled-by*=${this.control.id}]`
      );
    }

    // Intial configuration of attributes
    this.targets.forEach((target) => {
      this.configureAriaAttributes({ configOptions, target });
    });
  }

  /**
   * Configures the following ARIA attributes:
   *  [aria-controls]   Set the target ids on the control
   *  [aria-labelledby] Set the control ids on the target
   *  [aria-expanded]   Set so we can toggle this with the menu
   *  [aria-busy]       Set so we can toggle this with the menu transition
   *
   * @param {HTMLDivElement} target
   */
  configureAriaAttributes(configOptions = this.defaultConfigOptions) {
    // Get target from config
    const { target } = configOptions;

    // Private helper function for updating attribute values
    const updateAttr = (value, id) => {
      // If no current value, add target id
      if (!value) {
        value = id;
      } else {
        // There is a value set, add as a comma separated value
        value += ` ${id}`;
      }

      return value;
    };

    // Compute the ARIA values
    // Add target ids to aria-controls on control and add control ids to aria-labelledby on target
    const ariaControls = updateAttr(
      this.control.getAttribute("aria-controls"),
      target.id
    );
    const ariaLabelledby = updateAttr(
      target.getAttribute("aria-labelledby"),
      this.control.id
    );

    // Set attributes
    this.control.setAttribute("aria-controls", ariaControls);
    target.setAttribute("aria-labelledby", ariaLabelledby);

    // -- Start configuring other aria attributes as needed
    this.configureExpandableAttributes(configOptions);
  }

  /**
   * If the control is expandable, set the appopriate attribute on the target
   *
   * @param {defaultConfigOptions} configOptions
   */
  configureExpandableAttributes(
    { target, expandable, expanded } = this.defaultConfigOptions
  ) {
    if (expandable) {
      target.setAttribute("aria-expanded", expanded ?? false);
      target.setAttribute("aria-busy", false);
    }
  }

  /**
   * Determine appropriate Aria related operation to perform
   *
   * @param {validActions} action
   * @param {defaultConfigOptions} configOptions
   */
  modifyAria({ action, ...configOptions } = this.defaultConfigOptions) {
    // Trigger different aria function based on action value
    if (action) {
      switch (action.toUpperCase()) {
        case AriaControl.validActions.expand:
          this.ariaExpand(configOptions);
          break;
        case AriaControl.validActions.collapse:
          this.ariaExpand(configOptions);
          break;
        default:
          throw new Error(
            `"${action}" is an invalid action. Supply modifyAria with any of the following [${Object.values(this.validActions).split(", ")}]`
          );
      }
    } else {
      throw new Error(
        `Action was not provided. Supply modifyAria with any of the following [${Object.values(this.validActions).split(", ")}]`
      );
    }
  }

  /**
   * Handles an expansion type action on the target element
   *
   * @param {HTMLDivElement} target
   * @param {Boolean} expanded
   */
  ariaExpand({ target, expanded } = this.defaultConfigOptions) {
    if (target) {
      // If expanded is not provided, use current aria-expanded value
      const isExpanded =
        expanded ?? target.getAttribute("aria-expanded") === "true";
      target.setAttribute("aria-expanded", isExpanded);

      if (this.control) this.control.setAttribute("aria-pressed", isExpanded);
      target.setAttribute("aria-busy", true);

      // Clear any current aria-busy timeout
      clearTimeout(target.busyTimeout);

      // Create a timeout to change the aria-busy after the transition has ended
      target.busyTimeout = setTimeout(() => {
        target.setAttribute("aria-busy", false);
      }, 300);
    } else {
      throw new Error("Target was not supplied to this ariaExpand function");
    }
  }
}
